import { UPLOAD_ACCEPT } from '@tradeoutlet/shared';
import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, FileText, ImagePlus, Loader2, X } from 'lucide-react';
import { groupBySections, type FormField, type FormValue, type FormValues, type ListSection, type PickerFieldType } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useMembers, useTeams, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { discardUpload, uploadFile } from '@/lib/upload';
import { Avatar, Input, Select, Textarea } from '@/components/ui';
import { useBuildings } from '@/features/buildings/common';

const wide = (f: FormField) => f.type === 'long_text' || f.type === 'file' || f.type === 'multi_select' || (f.type === 'single_select' && (f.options?.length ?? 0) > 3);

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={clsx('flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition',
        active ? 'border-accent-line bg-accent-soft font-medium text-accent-fg' : 'border-slate-300 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900')}>
      {active && <Check className="h-3.5 w-3.5" />}{children}
    </button>
  );
}

/** t693u: what the picker says per kind ("Choose a department…", "Add a group…"). */
const PICK_NOUN: Record<PickerFieldType, string> = { person: 'a person', building: 'a building / site', department: 'a department', group: 'a group' };

function IdPicker({ field, value, onChange, kind }: { field: FormField; value: string[]; onChange: (v: string[]) => void; kind: PickerFieldType }) {
  const { members } = useMembers();
  const b = useBuildings();
  const { teams } = useTeams();
  const options = kind === 'person'
    ? members.filter(m => m.status === 'active').map(m => ({ id: m.user_id, name: m.display_name }))
    : kind === 'building' ? (b.data?.buildings ?? []).map(x => ({ id: x.id, name: x.name }))
    : teams.filter(t => t.kind === kind && !t.archived_at).map(t => ({ id: t.id, name: t.name })).sort((x, y) => x.name.localeCompare(y.name));
  const name = (id: string) => options.find(o => o.id === id)?.name ?? '…';
  if (!field.multiple) {
    return (
      <Select aria-label={field.label} value={value[0] ?? ''} onChange={e => onChange(e.target.value ? [e.target.value] : [])}>
        <option value="">Choose {PICK_NOUN[kind]}…</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </Select>
    );
  }
  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map(id => (
            <span key={id} className="flex items-center gap-1.5 rounded-full bg-slate-100 py-0.5 pl-1 pr-2 text-sm dark:bg-slate-800">
              {kind === 'person' && <Avatar size={18} id={id} name={name(id)} />}{name(id)}
              <button type="button" title="Remove" onClick={() => onChange(value.filter(x => x !== id))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <Select aria-label={field.label} value="" onChange={e => e.target.value && onChange([...value, e.target.value])}>
        <option value="">Add {PICK_NOUN[kind]}…</option>
        {options.filter(o => !value.includes(o.id)).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </Select>
    </div>
  );
}

/** Uploads a file for a form outside a channel (handovers, issue reports); returns the attachment. */
export type FormUpload = (file: File) => Promise<{ id: string; file_name: string }>;

function FileInput({ field, value, onChange, conversationId, labels, onLabel, upload }: {
  field: FormField; value: string[]; onChange: (v: string[]) => void; conversationId?: string; labels: Record<string, string>; onLabel: (id: string, name: string) => void;
  upload?: FormUpload;
}) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const max = Math.min(field.max ?? 10, 10);
  const add = async (files: FileList) => {
    if (!ws || (!conversationId && !upload)) { toast('Files can be added when posting in a channel', 'info'); return; }
    let next = [...value];
    for (const file of Array.from(files).slice(0, Math.max(0, max - next.length))) {
      setBusy(n => n + 1);
      try {
        const a = upload ? await upload(file) : await uploadFile(file, { workspace_id: ws, conversation_id: conversationId });
        onLabel(a.id, a.file_name);
        next = [...next, a.id];
        onChange(next);
      } catch (e) { toast(`Upload failed: ${errorMessage(e)}`, 'error'); } finally { setBusy(n => n - 1); }
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {value.map(id => (
        <span key={id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800">
          <FileText className="h-3.5 w-3.5 text-accent-fg" /><span className="max-w-48 truncate">{labels[id] ?? 'File'}</span>
          <button type="button" title="Remove file" aria-label={`Remove ${labels[id] ?? 'file'}`} className="-m-1 p-1" onClick={() => { onChange(value.filter(x => x !== id)); discardUpload(id); }}><X className="h-3 w-3" /></button>
        </span>
      ))}
      {busy > 0 && <span className="flex items-center gap-1 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading…</span>}
      {value.length < max && (
        <button type="button" onClick={() => ref.current?.click()} className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:border-accent-line hover:text-accent-fg dark:border-slate-600 dark:text-slate-300">
          <ImagePlus className="h-4 w-4" />Add photo or file
        </button>
      )}
      <input ref={ref} type="file" accept={UPLOAD_ACCEPT} multiple={max > 1} hidden onChange={e => { if (e.target.files) void add(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

export function FormFieldInput({ field: f, value, onChange, conversationId, labels = {}, onLabel = () => undefined, upload }: {
  field: FormField; value: FormValue | undefined; onChange: (v: FormValue | undefined) => void; conversationId?: string;
  labels?: Record<string, string>; onLabel?: (id: string, name: string) => void; upload?: FormUpload;
}) {
  const arr = Array.isArray(value) ? value : [];
  switch (f.type) {
    case 'short_text': return <Input aria-label={f.label} value={(value as string) ?? ''} placeholder={f.placeholder ?? ''} onChange={e => onChange(e.target.value)} />;
    case 'long_text': return <Textarea aria-label={f.label} rows={3} value={(value as string) ?? ''} placeholder={f.placeholder ?? ''} onChange={e => onChange(e.target.value)} />;
    case 'number': return (
      <div className="flex items-center gap-2">
        <Input aria-label={f.label} type="number" inputMode="decimal" className="w-36" min={f.min ?? undefined} max={f.max ?? undefined} value={value === undefined ? '' : String(value)}
          onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)} />
        {f.unit && <span className="text-sm text-slate-500">{f.unit}</span>}
      </div>
    );
    case 'single_select': return (f.options?.length ?? 0) <= 8 ? (
      <div role="radiogroup" aria-label={f.label} className="flex flex-wrap gap-1.5">
        {(f.options ?? []).map(o => <Chip key={o} active={value === o} onClick={() => onChange(value === o ? undefined : o)}>{o}</Chip>)}
      </div>
    ) : (
      <Select aria-label={f.label} value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)}>
        <option value="">Choose…</option>{(f.options ?? []).map(o => <option key={o}>{o}</option>)}
      </Select>
    );
    case 'multi_select': return (
      <div role="group" aria-label={f.label} className="flex flex-wrap gap-1.5">
        {(f.options ?? []).map(o => <Chip key={o} active={arr.includes(o)} onClick={() => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o])}>{o}</Chip>)}
      </div>
    );
    case 'date': return <Input aria-label={f.label} type="date" className="w-44" value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)} />;
    case 'time': return <Input aria-label={f.label} type="time" className="w-32" value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)} />;
    case 'datetime': return <Input aria-label={f.label} type="datetime-local" className="w-60" value={(value as string) ?? ''} onChange={e => onChange(e.target.value || undefined)} />;
    case 'checkbox': return (
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" className="h-4 w-4" checked={value === true} onChange={e => onChange(e.target.checked)} />{f.placeholder || 'Yes'}
      </label>
    );
    case 'person': case 'building': case 'department': case 'group': return <IdPicker field={f} value={arr} onChange={onChange} kind={f.type} />;
    case 'file': return <FileInput field={f} value={arr} onChange={onChange} conversationId={conversationId} labels={labels} onLabel={onLabel} upload={upload} />;
  }
}

/** t725u: a section heading where a form is filled in or read (title and optional description; never a question). */
export function FormSectionHeading({ title, description, className }: { title: string; description?: string | null; className?: string }) {
  return (
    <div className={clsx('min-w-0', className)} data-testid="form-section-heading">
      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      {description && <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-500">{description}</p>}
    </div>
  );
}

/** All fields of a form in a two-column grid with labels, help text and errors (t725u: under their section headings). */
export function FormFieldsEditor({ fields, sections, values, onChange, errors = {}, conversationId, labels, onLabel, single, upload }: {
  single?: boolean; fields: FormField[]; values: FormValues; onChange: (v: FormValues) => void; errors?: Record<string, string>; conversationId?: string;
  labels?: Record<string, string>; onLabel?: (id: string, name: string) => void; upload?: FormUpload;
  /** Section headings between the fields (channel forms). */
  sections?: readonly ListSection[] | null;
}) {
  if (sections?.length) {
    return (
      <div className="space-y-5">
        {groupBySections(fields, sections).map((g, i) => (
          <section key={g.section?.id ?? `lead${i}`} aria-label={g.section?.title || undefined} className="space-y-3">
            {g.section && <FormSectionHeading title={g.section.title} description={g.section.description} />}
            {g.items.length > 0 && <FormFieldsEditor fields={g.items} values={values} onChange={onChange} errors={errors} conversationId={conversationId} labels={labels} onLabel={onLabel} single={single} upload={upload} />}
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className={clsx('grid gap-x-5 gap-y-4', !single && 'sm:grid-cols-2')}>
      {fields.map(f => (
        <div key={f.id} className={clsx('min-w-0', !single && wide(f) && 'sm:col-span-2')} data-field={f.id}>
          <div className="mb-1 flex items-baseline gap-1 text-[13px] font-semibold text-slate-700 dark:text-slate-200">
            {f.label}{f.required && <span className="text-rose-500" title="Required">*</span>}
          </div>
          <FormFieldInput field={f} value={values[f.id]} conversationId={conversationId} labels={labels} onLabel={onLabel} upload={upload}
            onChange={v => { const next = { ...values }; if (v === undefined) delete next[f.id]; else next[f.id] = v; onChange(next); }} />
          {errors[f.id] ? <div className="mt-1 text-xs font-medium text-rose-600">{errors[f.id]}</div>
            : f.help ? <div className="mt-1 text-xs text-slate-500">{f.help}</div> : null}
        </div>
      ))}
    </div>
  );
}
